import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { MetricFormat, MetricGoal } from '@monitoring-panel/types';
import { api } from '@/services/api';

export function useMetricConfig(executionId: string) {
  return useQuery({
    queryKey: ['metric-config', executionId],
    queryFn: () => api.config.getMetrics(executionId),
    enabled: !!executionId,
  });
}

export function useAddPredefinedMetric(executionId: string) {
  const queryClient = useQueryClient();
  const { data: configData } = useMetricConfig(executionId);

  return useMutation({
    mutationFn: async (metricId: string) => {
      const config = configData?.config;
      const currentEnabled = config?.enabledOptionalMetrics || [];

      if (currentEnabled.includes(metricId)) {
        return config;
      }

      return api.config.updateMetrics(executionId, {
        enabledOptionalMetrics: [...currentEnabled, metricId],
        customMetrics: config?.customMetrics?.map((m) => ({
          id: m.id,
          name: m.name,
          formula: m.formula,
          format: m.format,
          decimals: m.decimals,
        })),
        starredMetrics: config?.starredMetrics,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metric-config', executionId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-metrics', executionId] });
      toast.success('Metric added');
    },
    onError: (error) => {
      toast.error(`Failed to add metric: ${error.message}`);
    },
  });
}

export function useRemoveMetric(executionId: string) {
  const queryClient = useQueryClient();
  const { data: configData } = useMetricConfig(executionId);

  return useMutation({
    mutationFn: async (metricId: string) => {
      const config = configData?.config;
      const isOptional = config?.enabledOptionalMetrics?.includes(metricId);

      // Remove from starred if it was starred
      const updatedStarred = config?.starredMetrics?.filter((id) => id !== metricId);

      if (isOptional) {
        return api.config.updateMetrics(executionId, {
          enabledOptionalMetrics: config?.enabledOptionalMetrics?.filter((id) => id !== metricId),
          customMetrics: config?.customMetrics?.map((m) => ({
            id: m.id,
            name: m.name,
            formula: m.formula,
            format: m.format,
            decimals: m.decimals,
          })),
          starredMetrics: updatedStarred,
        });
      } else {
        return api.config.updateMetrics(executionId, {
          enabledOptionalMetrics: config?.enabledOptionalMetrics,
          customMetrics: config?.customMetrics
            ?.filter((m) => m.id !== metricId)
            .map((m) => ({
              id: m.id,
              name: m.name,
              formula: m.formula,
              format: m.format,
              decimals: m.decimals,
            })),
          starredMetrics: updatedStarred,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metric-config', executionId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-metrics', executionId] });
      toast.success('Metric removed');
    },
    onError: (error) => {
      toast.error(`Failed to remove metric: ${error.message}`);
    },
  });
}

export function useCreateCustomMetric(executionId: string) {
  const queryClient = useQueryClient();
  const { data: configData } = useMetricConfig(executionId);

  return useMutation({
    mutationFn: async (metric: { name: string; formula: string; format: MetricFormat; decimals: number }) => {
      const config = configData?.config;

      const newCustomMetric = {
        id: `custom_${Date.now()}`,
        name: metric.name,
        formula: metric.formula,
        format: metric.format,
        decimals: metric.decimals,
      };

      const existingCustomMetrics = config?.customMetrics?.map((m) => ({
        id: m.id,
        name: m.name,
        formula: m.formula,
        format: m.format,
        decimals: m.decimals,
      })) || [];

      return api.config.updateMetrics(executionId, {
        enabledOptionalMetrics: config?.enabledOptionalMetrics,
        customMetrics: [...existingCustomMetrics, newCustomMetric],
        starredMetrics: config?.starredMetrics,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metric-config', executionId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-metrics', executionId] });
      toast.success('Custom metric created');
    },
    onError: (error) => {
      toast.error(`Failed to create metric: ${error.message}`);
    },
  });
}

export function useUpdateCustomMetric(executionId: string) {
  const queryClient = useQueryClient();
  const { data: configData } = useMetricConfig(executionId);

  return useMutation({
    mutationFn: async ({ metricId, metric }: { metricId: string; metric: { name: string; formula: string; format: MetricFormat; decimals: number } }) => {
      const config = configData?.config;

      const updatedCustomMetrics = config?.customMetrics?.map((m) => {
        if (m.id === metricId) {
          return {
            id: m.id,
            name: metric.name,
            formula: metric.formula,
            format: metric.format,
            decimals: metric.decimals,
          };
        }
        return {
          id: m.id,
          name: m.name,
          formula: m.formula,
          format: m.format,
          decimals: m.decimals,
        };
      }) || [];

      return api.config.updateMetrics(executionId, {
        enabledOptionalMetrics: config?.enabledOptionalMetrics,
        customMetrics: updatedCustomMetrics,
        starredMetrics: config?.starredMetrics,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metric-config', executionId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-metrics', executionId] });
      toast.success('Custom metric updated');
    },
    onError: (error) => {
      toast.error(`Failed to update metric: ${error.message}`);
    },
  });
}

export function useValidateFormula() {
  return useMutation({
    mutationFn: (params: {
      formula: string;
      availableColumns?: string[];
      sampleData?: Record<string, number>;
    }) => api.config.validateFormula(params),
  });
}

export function useUpdateMetricGoals(executionId: string) {
  const queryClient = useQueryClient();
  const { data: configData } = useMetricConfig(executionId);

  return useMutation({
    mutationFn: async (goals: Record<string, MetricGoal>) => {
      const config = configData?.config;
      return api.config.updateMetrics(executionId, {
        enabledOptionalMetrics: config?.enabledOptionalMetrics,
        customMetrics: config?.customMetrics?.map((m) => ({
          id: m.id,
          name: m.name,
          formula: m.formula,
          format: m.format,
          decimals: m.decimals,
        })),
        metricGoals: goals,
        starredMetrics: config?.starredMetrics,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metric-config', executionId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-metrics', executionId] });
      toast.success('Metric goals updated');
    },
    onError: (error) => {
      toast.error(`Failed to update goals: ${error.message}`);
    },
  });
}

export function useToggleStarredMetric(executionId: string) {
  const queryClient = useQueryClient();
  const { data: configData } = useMetricConfig(executionId);

  return useMutation({
    mutationFn: async (metricId: string) => {
      const config = configData?.config;
      const currentStarred = config?.starredMetrics || [];
      const isStarred = currentStarred.includes(metricId);

      const updatedStarred = isStarred
        ? currentStarred.filter((id) => id !== metricId)
        : [...currentStarred, metricId];

      return api.config.updateMetrics(executionId, {
        enabledOptionalMetrics: config?.enabledOptionalMetrics,
        customMetrics: config?.customMetrics?.map((m) => ({
          id: m.id,
          name: m.name,
          formula: m.formula,
          format: m.format,
          decimals: m.decimals,
        })),
        metricGoals: config?.metricGoals,
        starredMetrics: updatedStarred,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metric-config', executionId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-metrics', executionId] });
    },
    onError: (error) => {
      toast.error(`Failed to toggle starred metric: ${error.message}`);
    },
  });
}

export interface BulkCustomMetric {
  name: string;
  formula: string;
  format: MetricFormat;
  decimals: number;
  goal?: MetricGoal;
}

export function useBulkAddCustomMetrics(executionId: string) {
  const queryClient = useQueryClient();
  const { data: configData } = useMetricConfig(executionId);

  return useMutation({
    mutationFn: async (newMetrics: BulkCustomMetric[]) => {
      const config = configData?.config;

      const existingCustomMetrics = config?.customMetrics?.map((m) => ({
        id: m.id,
        name: m.name,
        formula: m.formula,
        format: m.format,
        decimals: m.decimals,
      })) || [];

      const baseTimestamp = Date.now();
      const created = newMetrics.map((m, idx) => ({
        id: `custom_${baseTimestamp}_${idx}`,
        name: m.name,
        formula: m.formula,
        format: m.format,
        decimals: m.decimals,
      }));

      const mergedGoals: Record<string, MetricGoal> = { ...(config?.metricGoals ?? {}) };
      newMetrics.forEach((m, idx) => {
        if (m.goal) {
          mergedGoals[created[idx].id] = m.goal;
        }
      });

      return api.config.updateMetrics(executionId, {
        enabledOptionalMetrics: config?.enabledOptionalMetrics,
        customMetrics: [...existingCustomMetrics, ...created],
        metricGoals: mergedGoals,
        starredMetrics: config?.starredMetrics,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['metric-config', executionId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-metrics', executionId] });
      toast.success(`Added ${variables.length} metric${variables.length === 1 ? '' : 's'}`);
    },
    onError: (error) => {
      toast.error(`Failed to add metrics: ${error.message}`);
    },
  });
}
